import type { Asset } from '../types'
import { getStorageUrl } from './api'
import { isAudioAsset, isImageAsset, isSetAsset } from './assetUtils'
import { getStillImagePreviewUri, type StillImagePreviewSelectionOptions } from './stillImagePreview'
import { resolveAssetVideoPlaybackSource } from './videoPlaybackSource'
import { isExrAsset } from './upscaleOperation'

export {
  STILL_IMAGE_PREVIEW_TIERS,
  getStillImagePreviewUri,
  getStillImagePreviewTier,
  hasStillImageThumbnailMetadata,
  hasThumbnailVariants,
  type StillImagePreviewSelectionOptions,
} from './stillImagePreview'

// Re-export type guards from assetUtils for backwards compatibility
// These are the single source of truth - do not duplicate implementations here
export { getAssetType, isAudioAsset, isVideoAsset, isImageAsset, isSetAsset } from './assetUtils'

/**
 * Check if an asset is uploaded (vs generated).
 */
export function isUploadedAsset(asset: Asset): boolean {
  return asset.source === 'uploaded'
}

/**
 * Check if an asset is generated (vs uploaded).
 */
export function isGeneratedAsset(asset: Asset): boolean {
  return asset.source === 'generated' || asset.source === undefined
}

/**
 * Get the best available URI for an image asset.
 * Priority: enhancedUri > uri > thumbnailUri.
 *
 * After enhancement completes, uri and enhancedUri should be the same,
 * but this function handles legacy data where they may differ.
 */
export function getImageAssetUri(asset: Asset): string | undefined {
  return asset.enhancedUri || asset.uri || asset.thumbnailUri || undefined
}

/**
 * Get the best display URI for an asset's thumbnail.
 * For images/videos: prefers Martini-managed thumbnail variants.
 * For audio: prefers waveformUri, then thumbnailUri.
 */
export function getAssetDisplayUri(
  asset: Asset | undefined | null,
  options?: StillImagePreviewSelectionOptions,
): string | undefined {
  if (!asset) return undefined
  if (isAudioAsset(asset)) {
    return asset.waveformUri || asset.thumbnailUri || undefined
  }
  return getStillImagePreviewUri(asset, options)
}

export function getAssetDisplayName(
  assetNames: ReadonlyMap<string, unknown> | undefined,
  asset: { id: string; shotNumber?: number | null } | undefined | null,
): string | undefined {
  if (!assetNames || !asset) return undefined

  const assetIdValue = assetNames.get(asset.id)
  const assetIdName = typeof assetIdValue === 'string' ? assetIdValue.trim() : undefined
  if (assetIdName) return assetIdName

  if (asset.shotNumber === undefined || asset.shotNumber === null) return undefined
  const shotNumberValue = assetNames.get(String(asset.shotNumber))
  const shotNumberName = typeof shotNumberValue === 'string' ? shotNumberValue.trim() : undefined
  return shotNumberName || undefined
}

/**
 * Get the playback URL for an asset.
 * For videos: uses the configured effective proxy resolution.
 * For images: prefers enhancedUri (AI-enhanced) over uri (full-size) over thumbnailUri (fallback).
 * Returns undefined if no valid URL is available.
 *
 * @param asset - The asset to get the playback URL for
 * @returns The full storage URL for playback, or undefined
 */
export function getAssetPlaybackUrl(asset: Asset | undefined | null): string | undefined {
  if (!asset) return undefined

  // Determine if this is an image asset
  const isImage = isImageAsset(asset)

  // For images, prefer enhancedUri (AI-enhanced) over uri (full-size), fall back to thumbnailUri
  if (isImage) {
    const uri = getImageAssetUri(asset)
    return uri ? getStorageUrl(uri) : undefined
  }

  if (isExrAsset(asset) && !asset.proxyUri) {
    return undefined
  }

  const videoSource = resolveAssetVideoPlaybackSource(asset)
  if (videoSource) {
    return videoSource.url
  }

  // For audio, prefer proxyUri (transcoded) over original uri
  const uri = asset.proxyUri || asset.uri
  return uri ? getStorageUrl(uri) : undefined
}

/**
 * True when the expanded preview carousel has a usable surface for this asset.
 * EXR masters are valid even without a playable URL or thumbnail because the
 * expanded video modal renders a download-only card.
 */
export function canOpenAssetPreview(asset: Asset | undefined | null): boolean {
  if (!asset) return false
  if (isSetAsset(asset) || isExrAsset(asset)) return true
  return !!getAssetPlaybackUrl(asset) || !!getAssetDisplayUri(asset)
}
